/**
 * Client Master Account soft-delete / restore / permanent-delete UI helpers.
 * Keep confirmation rules aligned with kk-admin-sa-governance client-lifecycle.
 */

export const CLIENT_LIFECYCLE_MIN_REASON_LENGTH = 12;
export const CLIENT_PERMANENT_DELETE_CONFIRM_TOKEN = "DELETE";
/** Reason code Archive stamps on every relationship it revokes. */
export const CLIENT_ARCHIVE_REVOKE_REASON = "master-account-soft-deleted";

export type ClientLifecycleView = "active" | "archived";

export type ClientArtifactType =
  | "BrandMembership"
  | "LocationAssignment"
  | "UserMembership"
  | "Region";

/** One relationship row Archive took down and Restore may revive. */
export interface ClientEligibleArtifact {
  artifactId: string;
  artifactType: ClientArtifactType | string;
  brandId?: string | null;
  /** Display-only Brand name from GET restore eligibility enrichment. */
  brandName?: string | null;
  locationId?: string | null;
  /** Display-only Location name from GET restore eligibility enrichment. */
  locationName?: string | null;
  regionId?: string | null;
  /** Display-only Region name from GET restore eligibility enrichment. */
  regionName?: string | null;
  auth0Sub?: string | null;
  role?: string | null;
  /** Display-only human role label (e.g. Owner). */
  roleLabel?: string | null;
  /** Display-only email from UserMembership.ownerEmail when available. */
  email?: string | null;
  /** Display-only person name when actually present on the membership. */
  displayName?: string | null;
  roleScope?: string | null;
  accessLevel?: string | null;
  revokedAt?: string | null;
  revokedReason?: string | null;
  deletedAt?: string | null;
  deletedReason?: string | null;
}

export interface ClientLifecycleBlocker {
  code: string;
  message: string;
  count?: number | null;
}

export interface ClientPurgeCount {
  label: string;
  value: number | null;
}

/** Authoritative Archive blast radius from POST …/archive with dryRun. */
export interface ClientArchivePreflight {
  masterAccountId?: string | null;
  businessName?: string | null;
  customerNumber?: string | null;
  kind?: string | null;
  status?: string | null;
  archived?: boolean;
  dryRun?: boolean;
  eligible: boolean;
  alreadyArchived?: boolean;
  blockers: ClientLifecycleBlocker[];
  checkStatus?: string;
  brandMembershipCount: number;
  locationAssignmentCount: number;
  userMembershipCount: number;
  regionCount: number;
  activeImpersonationTicketCount: number;
  zoneCount?: number | null;
  deviceCount?: number | null;
  affectedBrandCount?: number;
  affectedBrandIds?: string[];
  affectedLocationIds?: string[];
  warningTitle?: string;
  warningLead?: string;
  effects: string[];
  willRevokeBrandMembershipIds?: string[];
  willRevokeLocationAssignmentIds?: string[];
  willRevokeUserMembershipIds?: string[];
  willArchiveRegionIds?: string[];
}

export interface ClientPermanentDeletePreflight {
  masterAccountId?: string | null;
  businessName?: string | null;
  customerNumber?: string | null;
  kind?: string | null;
  status?: string | null;
  archived?: boolean;
  dryRun?: boolean;
  eligible: boolean;
  alreadyDeleted?: boolean;
  blockers: ClientLifecycleBlocker[];
  counts: ClientPurgeCount[];
  willRemove: string[];
  willRetain: string[];
}

function readPreflight(error: unknown): unknown {
  if (!error || typeof error !== "object" || !("data" in error)) return null;
  const data = (
    error as { data?: { data?: { preflight?: unknown }; preflight?: unknown } }
  ).data;
  return data?.data?.preflight ?? data?.preflight ?? null;
}

/** Pull the Archive preflight out of a 400/409 archive response. */
export function clientArchivePreflightFromError(
  error: unknown,
): ClientArchivePreflight | null {
  const preflight = readPreflight(error);
  if (!preflight || typeof preflight !== "object") return null;
  const p = preflight as ClientArchivePreflight;
  return typeof p.eligible === "boolean" ? p : null;
}

/** Pull the Permanent Delete preflight out of a 400/409 response. */
export function clientPreflightFromError(
  error: unknown,
): ClientPermanentDeletePreflight | null {
  const preflight = readPreflight(error);
  if (!preflight || typeof preflight !== "object") return null;
  const p = preflight as ClientPermanentDeletePreflight;
  return typeof p.eligible === "boolean" ? p : null;
}

export function extractApiErrorMessage(
  error: unknown,
  fallback = "Request failed",
): string {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object" &&
    "message" in error.data &&
    typeof (error as { data: { message: unknown } }).data.message === "string"
  ) {
    return (error as { data: { message: string } }).data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Default restore selection: every eligible artifact checked. */
export function defaultSelectedArtifactIds(
  eligible: ClientEligibleArtifact[],
): string[] {
  return (eligible || []).map((a) => a.artifactId).filter(Boolean);
}

/**
 * Explicit artifactIds payload for restore POST.
 * Always send an array (may be empty) — omitting it means "restore everything",
 * which would silently ignore the operator's unchecked boxes.
 */
export function artifactIdsForRestore(
  selectedIds: Iterable<string>,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of selectedIds) {
    const trimmed = String(id || "").trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

export function toggleArtifactSelection(
  selectedIds: string[],
  artifactId: string,
  checked: boolean,
): string[] {
  const id = String(artifactId || "").trim();
  if (!id) return selectedIds;
  if (checked) {
    return selectedIds.includes(id) ? selectedIds : [...selectedIds, id];
  }
  return selectedIds.filter((x) => x !== id);
}

export function canSubmitClientArchive(args: {
  businessName: string;
  nameConfirmation: string;
  reason: string;
}): boolean {
  const name = String(args.businessName || "");
  const confirmation = String(args.nameConfirmation || "");
  const reason = String(args.reason || "").trim();
  return (
    name.length > 0 &&
    confirmation === name &&
    reason.length >= CLIENT_LIFECYCLE_MIN_REASON_LENGTH
  );
}

export function canSubmitClientPermanentDelete(args: {
  businessName: string;
  nameConfirmation: string;
  confirmTyped: string;
  reason: string;
  eligible: boolean;
}): boolean {
  if (!args.eligible) return false;
  const name = String(args.businessName || "");
  const confirmation = String(args.nameConfirmation || "");
  const reason = String(args.reason || "").trim();
  return (
    name.length > 0 &&
    confirmation === name &&
    args.confirmTyped === CLIENT_PERMANENT_DELETE_CONFIRM_TOKEN &&
    reason.length >= CLIENT_LIFECYCLE_MIN_REASON_LENGTH
  );
}

export function clientAccessLevelLabel(
  roleScope: string | null | undefined,
): string {
  const scope = String(roleScope || "franchisee").toLowerCase();
  if (scope === "corporate") return "Full Access";
  if (scope === "franchisee") return "Limited Access";
  return roleScope ? String(roleScope) : "Limited Access";
}

/**
 * Auth0 subjects are never surfaced in the SA UI.
 * Prefer email/displayName from restore enrichment; this is a last-resort
 * non-ID placeholder when those fields are absent.
 */
export function maskUserSubject(sub: string | null | undefined): string {
  void sub;
  return "User";
}

/** Operator-facing group headings for the Restore checklist. */
export function clientArtifactTypeLabel(
  artifactType: string | null | undefined,
): string {
  switch (String(artifactType || "")) {
    case "BrandMembership":
      return "Brands";
    case "LocationAssignment":
      return "Locations";
    case "UserMembership":
      return "Users";
    case "Region":
      return "Regions";
    default:
      return "Other";
  }
}

function readableDisplay(value: string | null | undefined): string | null {
  const trimmed = String(value || "").trim();
  return trimmed || null;
}

/**
 * Row label for the restore checklist — operator language only.
 * Never shows auth0Sub, UUIDs, or membership IDs. Missing friendly names
 * use a generic category word (Brand / Location / User / Region).
 */
export function formatClientArtifactLabel(
  artifact: ClientEligibleArtifact,
): string {
  switch (String(artifact.artifactType || "")) {
    case "BrandMembership": {
      const name = readableDisplay(artifact.brandName) || "Brand";
      return `${name} — ${clientAccessLevelLabel(artifact.roleScope)}`;
    }
    case "LocationAssignment": {
      const location = readableDisplay(artifact.locationName) || "Location";
      const brand = readableDisplay(artifact.brandName);
      return brand ? `${location} — ${brand}` : location;
    }
    case "UserMembership": {
      const person =
        readableDisplay(artifact.displayName) ||
        readableDisplay(artifact.email) ||
        "User";
      const role =
        readableDisplay(artifact.roleLabel) ||
        readableDisplay(artifact.role) ||
        null;
      return role ? `${person} — ${role}` : person;
    }
    case "Region": {
      const region = readableDisplay(artifact.regionName) || "Region";
      const brand = readableDisplay(artifact.brandName);
      return brand ? `${region} — ${brand}` : region;
    }
    default:
      return "Access item";
  }
}

const ARTIFACT_TYPE_ORDER: string[] = [
  "BrandMembership",
  "LocationAssignment",
  "UserMembership",
  "Region",
];

export interface ClientArtifactGroup {
  artifactType: string;
  label: string;
  artifacts: ClientEligibleArtifact[];
}

/** Group eligible artifacts for the restore checklist, in a stable order. */
export function groupArtifactsByType(
  eligible: ClientEligibleArtifact[],
): ClientArtifactGroup[] {
  const byType = new Map<string, ClientEligibleArtifact[]>();
  for (const artifact of eligible || []) {
    if (!artifact?.artifactId) continue;
    const type = String(artifact.artifactType || "Other");
    const bucket = byType.get(type);
    if (bucket) bucket.push(artifact);
    else byType.set(type, [artifact]);
  }
  const types = [...byType.keys()].sort((a, b) => {
    const ai = ARTIFACT_TYPE_ORDER.indexOf(a);
    const bi = ARTIFACT_TYPE_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return types.map((artifactType) => ({
    artifactType,
    label: clientArtifactTypeLabel(artifactType),
    artifacts: byType.get(artifactType) || [],
  }));
}

/** Operator-facing singular/plural helper for Archive impact lines. */
export function countNoun(
  count: number,
  singular: string,
  plural: string,
): string {
  const n = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Archive blast-radius lines for the operator modal.
 * Translates preflight counts into plain English — no domain jargon.
 */
export function archiveOperatorImpactLines(
  preflight: ClientArchivePreflight | null,
): string[] {
  if (!preflight) return [];
  return [
    `${countNoun(preflight.userMembershipCount ?? 0, "user", "users")} will lose access`,
    `${countNoun(preflight.brandMembershipCount ?? 0, "brand", "brands")} will be removed from this Client`,
    `${countNoun(preflight.locationAssignmentCount ?? 0, "location", "locations")} will be removed from this Client`,
    `${countNoun(preflight.regionCount ?? 0, "region", "regions")} will be archived`,
    `${countNoun(preflight.activeImpersonationTicketCount ?? 0, "active Support Mode session", "active Support Mode sessions")} will be ended`,
  ];
}

/** @deprecated Prefer archiveOperatorImpactLines for operator UI. */
export function archiveBlastRadiusRows(
  preflight: ClientArchivePreflight | null,
): ClientPurgeCount[] {
  if (!preflight) return [];
  return [
    { label: "users losing access", value: preflight.userMembershipCount ?? 0 },
    {
      label: "brands removed from this Client",
      value: preflight.brandMembershipCount ?? 0,
    },
    {
      label: "locations removed from this Client",
      value: preflight.locationAssignmentCount ?? 0,
    },
    { label: "regions archived", value: preflight.regionCount ?? 0 },
    {
      label: "Support Mode sessions ended",
      value: preflight.activeImpersonationTicketCount ?? 0,
    },
  ];
}

/** True when the list row is an archived (soft-deleted) Client. */
export function isArchivedClientRow(row: {
  archived?: boolean;
  deletedAt?: string | null;
  status?: string;
}): boolean {
  if (!row) return false;
  return Boolean(row.archived || row.deletedAt || row.status === "deleted");
}
